import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { cn } from "components/lib/utils";
import { Avatar, AvatarFallback } from "components/shadcn/ui/avatar";
import { ArrowDown } from "lucide-react";

import ThreadMessageInput from "./threadMessageInput";
import BlockedMessageFooter from "./blockedMessageFooter";
import ConfirmDialog from "components/common/helper/confirmDialogue";

import {
  getInitials,
  getInitialsFromFullName,
  getTimeLabelWithDay,
} from "helpers";
import { ThreadDetailsResponse } from "components/messaging-thread/types";
import { useThreadDetailsMessages } from "./useThreadDetailsMessages";

interface Props {
  thread: ThreadDetailsResponse | null;
}

const ThreadDetailsMessages: React.FC<Props> = ({ thread }) => {
  const {
    messages,
    loading,
    sending,
    sendMessage,
    currentUserId,
    user,

    isBlocked,
    blockedByMe,
    blockedMe,

    showUnblockConfirm,
    setShowUnblockConfirm,
    unblocking,
    handleUnblock,
  } = useThreadDetailsMessages(thread);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const [showScrollDown, setShowScrollDown] = useState(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const isAtBottom =
      container.scrollHeight - container.scrollTop <=
      container.clientHeight + 50;

    setShowScrollDown(!isAtBottom);
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex-1 flex flex-col bg-white min-h-0 ">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[55vh]"
      >
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={40} width={100} />
          ))
        ) : messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            No messages yet
          </div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.senderId === currentUserId;

            return (
              <div
                key={msg.id}
                className={cn(
                  "flex items-end gap-2",
                  isMine ? "justify-end" : "justify-start"
                )}
              >
                {!isMine && (
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {getInitials(
                        thread?.participant.firstName || "",
                        thread?.participant.lastName || ""
                      )}
                    </AvatarFallback>
                  </Avatar>
                )}

                <div
                  className={cn(
                    "max-w-[70%] rounded-[16px] px-3 py-2 text-sm shadow-sm font-medium",
                    isMine
                      ? "bg-[var(--primary-400)] text-white border border-[#e92344]"
                      : "bg-[#EAF5FF] text-gray-900 border border-[#B3D4FF]"
                  )}
                >
                  <p
                    className={cn(
                      "text-left",
                      isMine ? "text-white" : "text-gray-900"
                    )}
                  >
                    {msg.body}
                  </p>
                  <p
                    className={cn(
                      "text-[10px] mt-1",
                      isMine
                        ? "text-right text-white"
                        : "text-left text-gray-600"
                    )}
                  >
                    {getTimeLabelWithDay(msg.createdAt)}
                  </p>
                </div>

                {isMine && (
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {user ? getInitialsFromFullName(user.name) : ""}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            );
          })
        )}

        <div ref={bottomRef} />
      </div>

      {showScrollDown && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-24 right-6 z-20 rounded-full bg-white shadow-md border p-2 hover:bg-gray-100 transition"
        >
          <ArrowDown size={18} />
        </button>
      )}

      {isBlocked ? (
        <BlockedMessageFooter
          blockedByMe={blockedByMe}
          blockedMe={blockedMe}
          onUnblock={() => setShowUnblockConfirm(true)}
          participantName={`${thread?.participant.firstName} ${thread?.participant.lastName}`}
        />
      ) : (
        <div className="border-t bg-white min-h-[10vh]">
          <ThreadMessageInput onSend={sendMessage} sending={sending} />
        </div>
      )}

      <ConfirmDialog
        open={showUnblockConfirm}
        onOpenChange={setShowUnblockConfirm}
        title="Unblock user?"
        description="You will be able to send messages again."
        confirmLabel={unblocking ? "Unblocking..." : "Unblock"}
        confirmVariant="primary"
        showCancelButton
        onConfirm={handleUnblock}
      />
    </div>
  );
};

export default ThreadDetailsMessages;
